import { FunctionComponent} from 'react'
import styled from 'styled-components'
import { useAppHandlers } from '../services/actions/app/useAppHandlers'
import Row from '../components/Row'
import Button from '../components/Button'
import DropdownMenu from '../components/dropdown/DropdownMenu'
import DropdownItem from '../components/dropdown/DropdownItem'
import { styles } from '../styles/themes/style'
import Column from '../components/Column'
import Text from '../components/Text'
import DropdownEmptyState from '../components/DropdownEmptyState'
import SvgFragment from './SvgFragment'
import React, { useRef, useEffect, useState } from 'react';
import TooltipWrapper from '../components/TooltipWrapper'

export const EllipsisDetect: React.FC<{ text: string; width?: string; deps?: any[]; textProps?: React.ComponentProps<typeof Text> }> = ({ text, width, textProps = {} }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isEllipsis, setIsEllipsis] = useState(false);
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if(el) setIsEllipsis(el.scrollWidth > el.clientWidth);
    }, [hovered]);
    
    return (
        <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ width: width }}>
            <TooltipWrapper show={hovered} tooltipText={isEllipsis ? text : ''}>
                <Text ref={ref} $ellipsis="0" {...textProps}>{text}</Text>
            </TooltipWrapper>
        </div>
    );
};

interface IFilterButtonProps{ 
    sidebar: string,
    savedFilters?: any[],
    numberOfFilters?: number,
    onSetSavedFilter?: (e: any) => void
    onRemoveSavedFilter?: (e: any) => void
    resetFilter?: () => void
    /** Called when the user opens the filter sidebar (e.g. to lazy-load filter options). */
    onBeforeOpen?: () => void
}

const FilterButton: FunctionComponent<IFilterButtonProps> = ({ sidebar, savedFilters, numberOfFilters, onSetSavedFilter, onRemoveSavedFilter, resetFilter, onBeforeOpen }) => {
    const { openSidebar } = useAppHandlers()
    return <Row gap='2px'>
        {/* todo: include loading when we are fetching mutliple condition filters etc */}
        <Button $mediumButton={!numberOfFilters} $mediumBigButton={!!numberOfFilters}  $SVGtype={numberOfFilters ? '' : 'filter'} $type='blue' onClickHandler={() => { onBeforeOpen?.(); openSidebar(sidebar); }}>
            {numberOfFilters ? <Number>{numberOfFilters}</Number> : null}
            {'Filter'}

            {(numberOfFilters && resetFilter) ?
            <ClearSvg  onClick={(e) => {e.stopPropagation(); resetFilter();}} className='clear-svg' width="12" height="12" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.96 11.825 6 7.785l4.04 4.04a.6.6 0 0 0 .846 0l.94-.939a.6.6 0 0 0 0-.847L7.785 6l4.039-4.04a.6.6 0 0 0 0-.846l-.939-.939a.6.6 0 0 0-.847 0L6 4.215 1.96.175a.6.6 0 0 0-.846 0l-.939.939a.6.6 0 0 0 0 .847L4.215 6 .175 10.04a.6.6 0 0 0 0 .847l.94.938a.6.6 0 0 0 .846 0z" fill="#8C96AD" fillRule="evenodd"/>
            </ClearSvg > : null}
        </Button>

        {savedFilters && <StyledDropdown 
            selectStyle
            tooltipMessage='Apply saved filters'
        >
            {savedFilters.length ? (
                <StyledColumn>
                    <div style={{padding: '8px 16px'}}><Text $heading6 $capitalizeAll>Saved Filters</Text></div>
                    {savedFilters.map((savedFilter: any, index: number) => (
                        <StyledDropdownItem key={index} onClickHandler={() => onSetSavedFilter && onSetSavedFilter(savedFilter)}>
                            <EllipsisDetect text={savedFilter.name} width="240px" />
                            <RemoveFilterIcon
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    onRemoveSavedFilter && onRemoveSavedFilter(savedFilter)
                                }}
                            >
                                <SvgFragment type='close'/>
                            </RemoveFilterIcon>
                        </StyledDropdownItem>
                    ))}
                </StyledColumn>
            ) : <DropdownEmptyState text={'No Saved Filters to Show'} />}
        </StyledDropdown>}
    </Row>
}

const StyledDropdown = styled(DropdownMenu)`
    border: none;
    &.dropdown {
        .dropdown-toggle {
            height: 34px;
            outline: none;
        }
        &:hover .dropdown-toggle {
            outline: 1px solid ${styles.colors.primary600}
        }
    }
`
const Number = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    font-family: NunitoSans;
    font-size: 12px;
    text-align: center;
    color: ${styles.colors.primary600};
    background: ${styles.colors.white};
`
const StyledColumn = styled(Column)`
    width: 320px;
    padding: 8px 0;
`
const ClearSvg = styled.svg`
    cursor: pointer;
    path {fill: ${styles.colors.red600};}
    &:hover path {fill: ${styles.colors.red500};}
`
const RemoveFilterIcon = styled.div`
    visibility: hidden;
    padding: 0 0 0 16px;

    & svg path { 
        fill: ${styles.colors.black300} !important; 
        stroke: none; 
    }

    &:hover {
        & svg path { 
            fill: ${styles.colors.primary600} !important; 
            stroke: none; 
        }
    }

`
const StyledDropdownItem = styled(DropdownItem)`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    color: ${styles.colors.black600};

    &:hover {
        background-color: #F6F8FB;
        color: ${styles.colors.black600};
    }

    &:hover ${RemoveFilterIcon} {
        visibility: visible;
    }
` 
export default FilterButton
